// React
import { useEffect, useMemo, useRef, useState } from "react";

// Hooks
import useCacheCleaner from "@Common/hooks/CacheCleaner";
import { useLocationId } from "@Common/hooks/Location/useLocationId";
import { useSubscriptions } from "@Common/context/subscriptions/Subscriptions";

// API
import { onUpdateSalesPerformanceSales } from "@DataPages/api/salesOperations/salesPerformance";
import { onCalculateSalesLogAnalytics, onUpdateSalesLog } from "@DataPages/api/salesOperations/salesLog/subscriptions";

// Helpers
import {
	getSalesOperationName,
	salesOperationQueryValues,
	salesOperationSubscriptionValues,
} from "@DataPages/components/SalesOperations/constants";

// Components
import BodyLayer from "@Layouts/components/Layers/BodyLayer.tsx";
import CollapsibleLeftPanel from "@Layouts/components/CollapsibleLeftPanel";
import CommissionSideBar from "@Layouts/components/SideBars/CommissionSideBar";
import SalesOperationsModel from "@DataPages/components/SalesOperations/SalesOperationsModel";

// Types
import { Nullable } from "@Common/interfaces/types";
import { ISidebarItem } from "@Common/interfaces/sidebar";

const QUERY_IDENTIFIERS = salesOperationQueryValues;
const SALES_PERFORMANCE_SUBSCRIPTION_KEY = getSalesOperationName("SUBSCRIPTION", "SALES_PERFORMANCE");
const SALES_LOG_SALES_SUBSCRIPTION_KEY = getSalesOperationName("SUBSCRIPTION", "SALES_LOG_SALES");
const SALES_LOG_ANALYTICS_SUBSCRIPTION_KEY = getSalesOperationName("SUBSCRIPTION", "SALES_LOG_ANALYTICS");

const SUBSCRIPTION_IDENTIFIERS = salesOperationSubscriptionValues;

export default function SalesOperationsLayer() {
	const { clearCache } = useCacheCleaner();
	const lastSelected = useRef<Nullable<ISidebarItem>>(null);
	const [selectedCommissionCycle, setSelectedCommissionCycle] = useState<Nullable<ISidebarItem>>(null);
	const { addSubscription, deleteSubscription } = useSubscriptions();

	const locationId = useLocationId();

	const sideBar = useMemo(() => {
		const sideSelection = function (selected?: ISidebarItem | null, isActiveCycle = false) {
			if (selected?._id !== lastSelected?.current?._id) {
				lastSelected.current = selected;
				setSelectedCommissionCycle(selected);

				if (isActiveCycle && selected?._id) {
					addSubscription(
						[SALES_PERFORMANCE_SUBSCRIPTION_KEY, selected?._id],
						[
							{
								context: "onUpdateSalesPerformanceSales",
								query: onUpdateSalesPerformanceSales,
								variables: {
									locationId,
									commissionCycleId: selected?._id,
								},
							},
						]
					);
					addSubscription(
						[SALES_LOG_SALES_SUBSCRIPTION_KEY, selected?._id],
						[
							{
								context: "onUpdateSalesLogSales",
								query: onUpdateSalesLog,
								variables: {
									commissionCycleId: selected?._id,
								},
							},
						]
					);
					addSubscription(
						[SALES_LOG_ANALYTICS_SUBSCRIPTION_KEY, selected?._id],
						[
							{
								context: "onCalculateSalesLogAnalytics",
								query: onCalculateSalesLogAnalytics,
								variables: {
									locationId,
									commissionCycleId: selected?._id,
								},
							},
						]
					);
				}
			}
		};

		return <CommissionSideBar callbackSelected={sideSelection} selectDefaultLastCommissionCycle />;
	}, [addSubscription, locationId]);

	useEffect(() => {
		return () => {
			setSelectedCommissionCycle(null);
			lastSelected.current = null;
			clearCache(QUERY_IDENTIFIERS);
			SUBSCRIPTION_IDENTIFIERS.forEach((key) => {
				deleteSubscription([key, selectedCommissionCycle?._id]);
			});
		};
	}, []);

	return (
		<BodyLayer floatingContent={<CollapsibleLeftPanel control={sideBar} />}>
			<SalesOperationsModel selectedCommissionCycle={selectedCommissionCycle} />
		</BodyLayer>
	);
}
